import {
  createFileRoute,
  Navigate,
  Outlet,
  useChildMatches,
  useParams,
} from '@tanstack/react-router';
import { z } from 'zod';

import { LoadingState } from '@/components/admin/LoadingState';
import { useSession } from '@/features/sessions/hooks/useSession';
import { requirePermission } from '@/lib/guards';

const legacySearchSchema = z.object({
  tab: z.enum(['overview', 'notes', 'case-reports', 'feedback']).optional(),
});

export const Route = createFileRoute('/app/sessions/$sessionId')({
  beforeLoad: () => {
    requirePermission('session:read');
  },
  validateSearch: legacySearchSchema,
  component: LegacySessionDetailRedirect,
  staticData: { crumb: 'Session' },
});

function LegacySessionDetailRedirect() {
  const { sessionId } = useParams({ from: '/app/sessions/$sessionId' });
  const childMatches = useChildMatches();
  const search = Route.useSearch();

  // Always call useSession — hooks must not be conditional.
  // When a child route is active we won't use the result, but the call order is stable.
  const isLeaf = childMatches.length === 0;
  const { data: session, isLoading, error } = useSession(isLeaf ? sessionId : undefined);

  // Child route matched (note, case-reports, etc.) — pass through.
  if (!isLeaf) return <Outlet />;

  // Leaf match — redirect into workspace.
  if (isLoading) return <LoadingState variant="detail" />;
  if (error || !session) {
    // Session not accessible — bounce to workspace home.
    return (
      <Navigate
        to="/app/sessions"
        search={{ segment: 'all' as const, drawerTab: 'overview' as const }}
        replace
      />
    );
  }
  return (
    <Navigate
      to="/app/sessions"
      search={{
        client: session.clientId ?? undefined,
        panel: 'session' as const,
        id: sessionId,
        drawerTab: search.tab ?? 'overview',
        segment: 'all' as const,
      }}
      replace
    />
  );
}
